import { useQuery } from '@tanstack/react-query';
import { gscApi } from '@/services/api/gsc';
import { useAuthStore } from '@/store/authStore';
import type { CampaignOrganicMetrics } from '@/types/gsc';

/**
 * Hook to get GSC connection status
 */
export function useGSCConnectionStatus() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['gsc-connection-status'],
    queryFn: () => gscApi.getConnectionStatus(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
}

/**
 * Hook to get campaign-level organic metrics for Performance Metrics display
 */
export function useCampaignOrganicMetrics(campaignId: string) {
  const { isAuthenticated } = useAuthStore();
  const connectionStatus = useGSCConnectionStatus();

  // Only fetch if GSC is enabled and has data
  const gscEnabled = connectionStatus.data?.enabled && connectionStatus.data?.has_data;

  return useQuery<CampaignOrganicMetrics>({
    queryKey: ['gsc-campaign-metrics', campaignId],
    queryFn: () => gscApi.getCampaignOrganicMetrics(campaignId),
    enabled: isAuthenticated && !!campaignId && gscEnabled === true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
}
